import React, { Component } from "react";

import Tooltip from "../tooltip";

import BlockImage from "./blockImage";
import { PRICE_SOURCE_CRAFTING, PRICE_SOURCE_FREE, resolveCraftingPrice } from "../../services/craftingPriceProvider";
import { createManualPriceProvider, normalizeMaterialKey } from "../../services/manualPriceProvider";
import {
  createOpsuchtMarketProvider,
  loadOpsuchtMarketPrices,
  PRICE_SOURCE_CACHE,
  PRICE_SOURCE_MISSING,
  PRICE_SOURCE_OPSUCHT_API,
} from "../../services/opsuchtMarketProvider";

import "./materials.css";

class Materials extends Component {
  state = {
    onlyMaxPerSplit: false,
    marketPrices: {},
    marketPriceSource: PRICE_SOURCE_MISSING,
    marketPriceCachedAt: null,
    marketPriceError: null,
    marketPricesLoading: false,
    manualPrices: {},
  };

  componentDidMount() {
    this.loadMarketPrices();
  }

  loadMarketPrices = async () => {
    this.setState({ marketPricesLoading: true });
    const marketPriceResult = await loadOpsuchtMarketPrices();
    this.setState({
      marketPrices: marketPriceResult.prices,
      marketPriceSource: marketPriceResult.source,
      marketPriceCachedAt: marketPriceResult.cachedAt,
      marketPriceError: marketPriceResult.error,
      marketPricesLoading: false,
    });
  };

  onOnlyMaxPerSplitChange = () => {
    this.setState((currentState) => ({
      // nb this method of passing currentState instead of using this.state... is prefered; TODO neaten up controller uses
      onlyMaxPerSplit: !currentState.onlyMaxPerSplit,
    }));
  };

  getMaterialsCount_nonZeroMaterialsItems() {
    const { coloursJSON, currentMaterialsData } = this.props;
    const { onlyMaxPerSplit } = this.state;
    const materialsCount = {};
    for (const colourSetId of Object.keys(coloursJSON)) {
      materialsCount[colourSetId] = 0;
    }
    for (const row of currentMaterialsData.maps) {
      for (const map of row) {
        for (const [colourSetId, materialCount] of Object.entries(map.materials)) {
          if (onlyMaxPerSplit) {
            materialsCount[colourSetId] = Math.max(materialsCount[colourSetId], materialCount);
          } else {
            materialsCount[colourSetId] += materialCount;
          }
        }
      }
    }
    return Object.entries(materialsCount)
      .filter(([_, value]) => value !== 0)
      .sort((first, second) => {
        return second[1] - first[1];
      });
  }

  getMaterialsCount_supportBlock() {
    const { currentMaterialsData } = this.props;
    const { onlyMaxPerSplit } = this.state;
    let supportBlockCount = 0;
    currentMaterialsData.maps.forEach((row) => {
      row.forEach((map) => {
        const count = map.supportBlockCount;
        if (onlyMaxPerSplit) {
          supportBlockCount = Math.max(supportBlockCount, count);
        } else {
          supportBlockCount += count;
        }
      });
    });
    return supportBlockCount;
  }

  formatMaterialCount = (count) => {
    const numberOfStacks = Math.floor(count / 64);
    const remainder = count % 64;
    const numberOfShulkers = count / 1728;
    return `${count.toString()}${
      numberOfStacks !== 0
        ? ` (${numberOfStacks.toString()}x64${remainder !== 0 ? ` + ${remainder.toString()}` : ""}${
            numberOfShulkers >= 1 ? `, ${numberOfShulkers.toFixed(2)} SB` : ""
          })`
        : ""
    }`;
  };

  formatPrice = (price) => {
    if (price === null || price === undefined) return "-";
    return new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(Math.round(price));
  };

  getPriceSourceLabel = (source) => {
    switch (source) {
      case PRICE_SOURCE_OPSUCHT_API:
        return "OPSUCHT API";
      case PRICE_SOURCE_CACHE:
        return "Cache";
      case "manual":
        return "Manuell";
      case PRICE_SOURCE_CRAFTING:
        return "Crafting";
      case PRICE_SOURCE_FREE:
        return "Kostenlos";
      default:
        return "Fehlend";
    }
  };

  getMarketStatusLabel = () => {
    const { marketPriceSource, marketPriceCachedAt, marketPriceError, marketPricesLoading } = this.state;
    if (marketPricesLoading) return "Lade OPSUCHT Preise...";
    if (marketPriceSource === PRICE_SOURCE_OPSUCHT_API) return "Preisquelle: OPSUCHT API";
    if (marketPriceSource === PRICE_SOURCE_CACHE) {
      const cachedAtText = marketPriceCachedAt === null ? "" : ` (${new Date(marketPriceCachedAt).toLocaleString("de-DE")})`;
      return `Preisquelle: Cache${cachedAtText}`;
    }
    return `Preisquelle: Fehlend${marketPriceError ? " - API nicht erreichbar" : ""}`;
  };

  getBlockNBTName(colourSetId, blockId) {
    const { coloursJSON, optionValue_version } = this.props;
    let blockNBTData = coloursJSON[colourSetId].blocks[blockId].validVersions[optionValue_version.MCVersion];
    if (typeof blockNBTData === "string") {
      blockNBTData = coloursJSON[colourSetId].blocks[blockId].validVersions[blockNBTData.slice(1)];
    }
    return blockNBTData.NBTName;
  }

  onManualPriceChange = (material) => (event) => {
    const normalizedMaterial = normalizeMaterialKey(material);
    const cleanedValue = event.target.value.replace(/[^\d.]/g, "");
    this.setState((currentState) => ({
      manualPrices: {
        ...currentState.manualPrices,
        [normalizedMaterial]: cleanedValue,
      },
    }));
  };

  resolveMaterialPrice(material) {
    const { marketPrices, marketPriceSource, manualPrices } = this.state;
    const opsuchtProvider = createOpsuchtMarketProvider({ prices: marketPrices, source: marketPriceSource });
    const manualProvider = createManualPriceProvider(manualPrices);
    const providers = [opsuchtProvider, manualProvider];
    const craftedPrice = resolveCraftingPrice(material, providers);
    if (craftedPrice !== null) {
      return craftedPrice;
    }
    const opsuchtPrice = opsuchtProvider.getPrice(material);
    if (opsuchtPrice !== null) {
      return { price: opsuchtPrice, source: marketPriceSource };
    }
    const manualPrice = manualProvider.getPrice(material);
    if (manualPrice !== null) {
      return { price: manualPrice, source: "manual" };
    }
    return { price: null, source: PRICE_SOURCE_MISSING };
  }

  colourSetIdAndBlockIdFromNBTName(blockName) {
    const { coloursJSON, optionValue_version } = this.props;
    for (const [colourSetId, colourSet] of Object.entries(coloursJSON)) {
      for (const [blockId, block] of Object.entries(colourSet.blocks)) {
        if (!(optionValue_version.MCVersion in block.validVersions)) {
          continue;
        }
        let blockNBTData = block.validVersions[optionValue_version.MCVersion];
        if (typeof blockNBTData === "string") {
          // this is of the form eg "&1.12.2"
          blockNBTData = block.validVersions[blockNBTData.slice(1)];
        }
        if (
          Object.keys(blockNBTData.NBTArgs).length === 0 && // no exotic blocks for noobline
          blockName.toLowerCase() === blockNBTData.NBTName.toLowerCase()
        ) {
          return { colourSetId, blockId };
        }
      }
    }
    return null; // if block not found
  }

  render() {
    const { getLocaleString, coloursJSON, optionValue_supportBlock, currentMaterialsData } = this.props;
    const { onlyMaxPerSplit, manualPrices, marketPricesLoading } = this.state;
    const nonZeroMaterialsItems = this.getMaterialsCount_nonZeroMaterialsItems();
    const supportBlockCount = this.getMaterialsCount_supportBlock();
    const supportBlockIds = this.colourSetIdAndBlockIdFromNBTName(optionValue_supportBlock);
    let totalMaterialCost = 0;
    let missingPriceCount = 0;
    const materialRows = nonZeroMaterialsItems.map(([colourSetId, materialCount]) => {
      const blockId = currentMaterialsData.currentSelectedBlocks[colourSetId];
      const material = this.getBlockNBTName(colourSetId, blockId);
      const resolvedPrice = this.resolveMaterialPrice(material);
      if (resolvedPrice.price === null) {
        missingPriceCount += 1;
      } else {
        totalMaterialCost += resolvedPrice.price * materialCount;
      }
      return { colourSetId, blockId, material, materialCount, resolvedPrice };
    });
    let supportBlockRow = null;
    if (supportBlockCount !== 0) {
      const resolvedPrice = this.resolveMaterialPrice(optionValue_supportBlock);
      if (resolvedPrice.price === null) {
        missingPriceCount += 1;
      } else {
        totalMaterialCost += resolvedPrice.price * supportBlockCount;
      }
      supportBlockRow = { material: optionValue_supportBlock, materialCount: supportBlockCount, resolvedPrice };
    }
    return (
      <div className="section materialsDiv">
        <h2>{getLocaleString("MATERIALS/TITLE")}</h2>
        <div className="materialsToolbar">
          <label className="materialsToggle">
            <Tooltip tooltipText={getLocaleString("MATERIALS/SHOW-PER-SPLIT-TT")}>
              <span>
                {getLocaleString("MATERIALS/SHOW-PER-SPLIT")}
                {":"}
              </span>
            </Tooltip>{" "}
            <input type="checkbox" checked={onlyMaxPerSplit} onChange={this.onOnlyMaxPerSplitChange} />
          </label>
          <button type="button" onClick={this.loadMarketPrices} disabled={marketPricesLoading}>
            Preise aktualisieren
          </button>
        </div>
        <p className="materialsPriceSource">{this.getMarketStatusLabel()}</p>
        <div className="materialsTableWrap">
          <table id="materialtable">
            <tbody>
              <tr>
                <th>{getLocaleString("MATERIALS/BLOCK")}</th>
                <th>{getLocaleString("MATERIALS/AMOUNT")}</th>
                <th>Preis/Stk.</th>
                <th>Quelle</th>
                <th>Manuell</th>
                <th>Kosten</th>
              </tr>
              {supportBlockRow !== null && (
                <tr>
                  <th>
                    <Tooltip tooltipText={getLocaleString("MATERIALS/PLACEHOLDER-BLOCK-TT")}>
                      <BlockImage
                        getLocaleString={getLocaleString}
                        coloursJSON={coloursJSON}
                        colourSetId={supportBlockIds === null ? "64" : supportBlockIds.colourSetId}
                        blockId={supportBlockIds === null ? "2" : supportBlockIds.blockId}
                      />
                    </Tooltip>
                  </th>
                  <th>{this.formatMaterialCount(supportBlockRow.materialCount)}</th>
                  <th>{this.formatPrice(supportBlockRow.resolvedPrice.price)}</th>
                  <th>
                    <span className={`materialSourceBadge materialSourceBadge_${supportBlockRow.resolvedPrice.source}`}>
                      {this.getPriceSourceLabel(supportBlockRow.resolvedPrice.source)}
                    </span>
                  </th>
                  <th>
                    <input
                      className="manualMaterialPriceInput"
                      type="number"
                      min="0"
                      step="1"
                      value={manualPrices[normalizeMaterialKey(supportBlockRow.material)] || ""}
                      onChange={this.onManualPriceChange(supportBlockRow.material)}
                      placeholder="Fallback"
                    />
                  </th>
                  <th>
                    {supportBlockRow.resolvedPrice.price === null ? "-" : this.formatPrice(supportBlockRow.resolvedPrice.price * supportBlockRow.materialCount)}
                  </th>
                </tr>
              )}
              {materialRows.map(({ colourSetId, blockId, material, materialCount, resolvedPrice }) => {
                return (
                  <tr key={colourSetId}>
                    <th>
                      <Tooltip tooltipText={coloursJSON[colourSetId].blocks[blockId].displayName}>
                        <BlockImage coloursJSON={coloursJSON} colourSetId={colourSetId} blockId={blockId} />
                      </Tooltip>
                    </th>
                    <th>{this.formatMaterialCount(materialCount)}</th>
                    <th>{this.formatPrice(resolvedPrice.price)}</th>
                    <th>
                      <span className={`materialSourceBadge materialSourceBadge_${resolvedPrice.source}`}>{this.getPriceSourceLabel(resolvedPrice.source)}</span>
                    </th>
                    <th>
                      <input
                        className="manualMaterialPriceInput"
                        type="number"
                        min="0"
                        step="1"
                        value={manualPrices[normalizeMaterialKey(material)] || ""}
                        onChange={this.onManualPriceChange(material)}
                        placeholder="Fallback"
                      />
                    </th>
                    <th>{resolvedPrice.price === null ? "-" : this.formatPrice(resolvedPrice.price * materialCount)}</th>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="materialsCostSummary">
          <span>Materialkosten</span>
          <strong>{this.formatPrice(totalMaterialCost)}</strong>
          {missingPriceCount !== 0 ? <small>{`${missingPriceCount} Preis${missingPriceCount === 1 ? "" : "e"} fehlend`}</small> : null}
        </div>
      </div>
    );
  }
}

export default Materials;
